import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMicrophoneErrorReason,
  type MicrophoneErrorReason,
} from '../voice/microphoneError';
import { getSupportedWebmOpusMimeType } from '../voice/recording';

export type WebmOpusRecordingCompletePayload = {
  blob: Blob;
  mimeType: string;
};

export type WebmOpusRecordingStatus =
  | 'idle'
  | 'recording'
  | 'ready'
  | 'unsupported'
  | 'error';

/** Why recording failed; the UI turns it into localized text. */
export type WebmOpusRecordingError =
  | `microphone:${MicrophoneErrorReason}`
  | 'recordingFailed'
  | 'unknownMime';

export type UseWebmOpusRecordingResult = {
  status: WebmOpusRecordingStatus;
  error: WebmOpusRecordingError | null;
  /** Длительность записи в мс (обновляется во время записи) */
  elapsedMs: number;
  /** Длительность последней сохранённой записи в мс (для UI — у WebM из MediaRecorder `audio.duration` часто Infinity) */
  recordingDurationMs: number | null;
  /** Готовый фрагмент после остановки записи */
  blob: Blob | null;
  /** MIME, с которым был создан `MediaRecorder` и Blob */
  mimeType: string | null;
  /** Поток микрофона во время записи (для визуализации и т.п.) */
  mediaStream: MediaStream | null;
  canRecord: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  /** Отмена во время записи: поток останавливается, Blob не сохраняется */
  cancelRecording: () => void;
  /** Сброс готового Blob и возврат в idle */
  clearRecording: () => void;
};

const TIMESLICE_MS = 250;

function stopStreamTracks(stream: MediaStream | null) {
  stream?.getTracks().forEach(track => {
    track.stop();
  });
}

export type UseWebmOpusRecordingOptions = {
  onRecordingComplete?: (payload: WebmOpusRecordingCompletePayload) => void;
};

export function useWebmOpusRecording(
  options: UseWebmOpusRecordingOptions = {}
): UseWebmOpusRecordingResult {
  const { onRecordingComplete } = options;
  const onCompleteRef = useRef(onRecordingComplete);
  onCompleteRef.current = onRecordingComplete;
  const [status, setStatus] = useState<WebmOpusRecordingStatus>('idle');
  const [error, setError] = useState<WebmOpusRecordingError | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recordingDurationMs, setRecordingDurationMs] = useState<number | null>(
    null
  );
  const [blob, setBlob] = useState<Blob | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const sessionMimeRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);
  const failedRef = useRef(false);
  const activeRef = useRef(true);
  const requestIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startAtRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupStream = useCallback(() => {
    stopStreamTracks(streamRef.current);
    streamRef.current = null;
    setMediaStream(null);
  }, []);

  const clearRecording = useCallback(() => {
    setBlob(null);
    setMimeType(null);
    setRecordingDurationMs(null);
    setError(null);
    setElapsedMs(0);
    setMediaStream(null);
    setStatus('idle');
  }, []);

  const cancelRecording = useCallback(() => {
    requestIdRef.current += 1;
    cancelledRef.current = true;
    clearTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    } else {
      cleanupStream();
      mediaRecorderRef.current = null;
      chunksRef.current = [];
      setElapsedMs(0);
      setStatus('idle');
      cancelledRef.current = false;
    }
  }, [cleanupStream, clearTimer]);

  const stopRecording = useCallback(() => {
    cancelledRef.current = false;
    clearTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
  }, [clearTimer]);

  const startRecording = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const mime = getSupportedWebmOpusMimeType();
    if (!mime) {
      setStatus('unsupported');
      setError(null);
      return;
    }

    clearRecording();
    cancelledRef.current = false;
    failedRef.current = false;
    sessionMimeRef.current = mime;
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Permission can resolve after the owning chat modal has closed.
      if (!activeRef.current || requestId !== requestIdRef.current) {
        stopStreamTracks(stream);
        return;
      }
      streamRef.current = stream;
      setMediaStream(stream);

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        failedRef.current = true;
      };

      // Also fires when the recorder stops by itself, e.g. the microphone
      // is unplugged, so the elapsed-time timer must stop here too.
      recorder.onstop = () => {
        clearTimer();
        cleanupStream();
        mediaRecorderRef.current = null;
        const usedMime = sessionMimeRef.current;
        sessionMimeRef.current = null;

        if (cancelledRef.current) {
          cancelledRef.current = false;
          chunksRef.current = [];
          setElapsedMs(0);
          setStatus('idle');
          return;
        }

        if (failedRef.current) {
          failedRef.current = false;
          chunksRef.current = [];
          setElapsedMs(0);
          setStatus('error');
          setError('recordingFailed');
          return;
        }

        const parts = chunksRef.current;
        chunksRef.current = [];
        const durationMs = Math.max(0, Date.now() - startAtRef.current);
        setRecordingDurationMs(durationMs);
        setElapsedMs(0);

        if (!usedMime) {
          setRecordingDurationMs(null);
          setStatus('error');
          setError('unknownMime');
          return;
        }

        const nextBlob = new Blob(parts, { type: usedMime });
        setBlob(nextBlob);
        setMimeType(usedMime);
        setStatus('ready');
        setError(null);
        onCompleteRef.current?.({ blob: nextBlob, mimeType: usedMime });
      };

      recorder.start(TIMESLICE_MS);
      setStatus('recording');
      setError(null);
      startAtRef.current = Date.now();
      setElapsedMs(0);
      clearTimer();
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startAtRef.current);
      }, 200);
    } catch (err) {
      if (!activeRef.current || requestId !== requestIdRef.current) {
        return;
      }
      cleanupStream();
      mediaRecorderRef.current = null;
      sessionMimeRef.current = null;
      chunksRef.current = [];
      setStatus('error');
      setError(`microphone:${getMicrophoneErrorReason(err)}`);
    }
  }, [cleanupStream, clearRecording, clearTimer]);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      requestIdRef.current += 1;
      clearTimer();
      cancelledRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state === 'recording') {
        recorder.stop();
      }
      cleanupStream();
      mediaRecorderRef.current = null;
    };
  }, [cleanupStream, clearTimer]);

  const supported = getSupportedWebmOpusMimeType() !== null;

  return {
    status,
    error,
    elapsedMs,
    recordingDurationMs,
    blob,
    mimeType,
    mediaStream,
    canRecord: supported,
    startRecording,
    stopRecording,
    cancelRecording,
    clearRecording,
  };
}
